<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { isEqual } from 'lodash'
import { bringDrawLayersToTop, ensureZLayer } from '@/composables/useZLayers'
import { registerHoverLayer, unregisterHoverLayer } from '@/utils/mapbox/hoverCursor'

const emit = defineEmits(['click', 'mounted', 'loaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  source: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: true,
    validator: (value) =>
      ['fill', 'line', 'symbol', 'circle', 'heatmap', 'fill-extrusion', 'raster', 'hillshade', 'background', 'sky', 'slot'].includes(value),
  },
  options: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  beforeId: {
    type: String,
    required: false,
    default: null,
  },
  slot: {
    type: String,
    required: false,
    default: null,
  },
  groupId: {
    type: String,
    required: false,
    default: null,
  },
  interactive: {
    type: Boolean,
    required: false,
    default: true,
  },
})

const onLayerClick = (e) => emit('click', e)
const zIndexOf = (id) => (id && /^z-\d+$/.test(id) ? Number(id.slice(2)) : null)
let listenersAttached = false

const addLayerListeners = (layer) => {
  if (listenersAttached) return
  listenersAttached = true
  registerHoverLayer(props.map, layer)
  props.map.on('click', layer, onLayerClick)
}

const removeLayerListeners = (layer) => {
  if (!listenersAttached) return
  listenersAttached = false
  unregisterHoverLayer(props.map, layer)
  props.map.off('click', layer, onLayerClick)
}

let sourceCheckInterval = null

const addLayer = async () => {
  if (!props.map.getLayer(props.id)) {
    const layerOptions = {
      id: props.id,
      type: props.type,
      ...props.options,
      minzoom: 0,
      maxzoom: 24,
    }

    if (props.slot) {
      layerOptions.slot = props.slot
    }

    if (props.source) {
      layerOptions.source = props.source
    }

    if (props.groupId) {
      layerOptions.metadata = {
        ...(layerOptions.metadata || {}),
        groupId: props.groupId,
      }
    }

    let anchored = false
    try {
      const z = zIndexOf(props.beforeId)
      if (z !== null) ensureZLayer(props.map, z)
      anchored = !!(props.beforeId && props.map.getLayer(props.beforeId))
      if (anchored) {
        props.map.addLayer(layerOptions, props.beforeId)
      } else {
        props.map.addLayer(layerOptions)
      }
    } catch (err) {
      const msg = err?.message ?? ''
      const transient = msg.includes('Style is not done loading') || msg.includes('Source') || msg.includes('style')
      if (!transient) throw err
      props.map.once('styledata', addLayer)
      return
    }

    // Inserting before a z-anchor can never land above the draw layers; only an
    // appended layer (no anchor yet) needs the full style scan.
    if (!anchored) bringDrawLayersToTop(props.map)

    // Only add listeners for interactive layers when interactive prop is true
    const nonInteractiveTypes = ['slot', 'background', 'sky', 'raster', 'hillshade']
    if (props.interactive && !nonInteractiveTypes.includes(props.type)) {
      addLayerListeners(props.id)
    }


    const waitForSourceLoaded = (map, sourceId, interval = 100) => {
      if (sourceCheckInterval) clearInterval(sourceCheckInterval)
      sourceCheckInterval = setInterval(() => {
        // The instance may be destroyed while polling (tile removed/recreated);
        // map.style is gone then and getSource would throw.
        if (!map.style || !map.getSource(sourceId)) {
          clearInterval(sourceCheckInterval)
          sourceCheckInterval = null
          return
        }

        if (map.isSourceLoaded(sourceId)) {
          clearInterval(sourceCheckInterval)
          sourceCheckInterval = null
          emit('loaded')
        }
      }, interval)
    }

    waitForSourceLoaded(props.map, props.source)
  } else {
    console.warn(`Layer '${props.id}' already exists on the map.`)
  }

  setTimeout(() => {
    emit('mounted')
  }, 1000)
}

const removeLayer = () => {
  // During tile teardown the parent MapCanvas may have already removed the
  // instance's style; getLayer() then dereferences an undefined style and throws.
  if (!props.map?.style) return
  try {
    removeLayerListeners(props.id)
    if (props.map.getLayer(props.id)) {
      if (props.image) {
        props.map.removeImage(props.id)
      }
      props.map.removeLayer(props.id)
    }
  } catch (_) { /* instance being disposed */ }
}

watch(() => props.options, (newOpts, oldOpts) => {
  if (!props.map?.getLayer(props.id) || isEqual(newOpts, oldOpts)) return
  const newLayout = newOpts?.layout || {}
  const oldLayout = oldOpts?.layout || {}
  for (const [key, val] of Object.entries(newLayout)) {
    if (!isEqual(val, oldLayout[key])) {
      try { props.map.setLayoutProperty(props.id, key, val) } catch (_) {}
    }
  }
  const newPaint = newOpts?.paint || {}
  const oldPaint = oldOpts?.paint || {}
  for (const [key, val] of Object.entries(newPaint)) {
    if (!isEqual(val, oldPaint[key])) {
      try { props.map.setPaintProperty(props.id, key, val) } catch (_) {}
    }
  }
})

// Watch for beforeId changes and move layer accordingly
// This is the key fix for layer ordering when drag-drop reorders layers
watch(() => props.beforeId, (newBeforeId, oldBeforeId) => {
  if (newBeforeId !== oldBeforeId && props.map && props.map.getLayer(props.id)) {
    try {
      const z = zIndexOf(newBeforeId)
      if (z !== null) ensureZLayer(props.map, z)
      const targetLayer = newBeforeId && props.map.getLayer(newBeforeId)
        ? newBeforeId
        : undefined
      props.map.moveLayer(props.id, targetLayer)
      if (!targetLayer) bringDrawLayersToTop(props.map)
    } catch (e) {
      console.warn(`Failed to move layer ${props.id}:`, e)
    }
  }
})

// Map-level listeners survive setStyle; only the layer itself needs re-adding.
onMounted(async () => {
  await addLayer()
  props.map.on('style.load', addLayer)
})

onUnmounted(() => {
  if (sourceCheckInterval) {
    clearInterval(sourceCheckInterval)
    sourceCheckInterval = null
  }
  removeLayer()
  props.map.off('style.load', addLayer)
  props.map.off('styledata', addLayer)
})
</script>

<template>
  <slot></slot>
</template>
